import { Loading } from "../../app/Status";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import {
  ASSET_PLAN_TYPES,
  formatEuro,
  formatIntervalOf,
  nextDue,
  OBLIGATION_BASES,
  OBLIGATION_BASIS_APP_LABELS,
  OBLIGATION_STATUS_LABELS,
  parseIntervalInput,
  perYear,
  PLAN_TYPE_LABELS,
  type AssetObligationDto,
  type AssetObligationsResponse,
  type ObligationBasis,
  type ObligationKind,
} from "@klantpunt/shared";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { parseEuro } from "../../pages/Platform";
import { Drawer } from "../mjop/planning/ui";
import { fmtDay } from "./ObligationSection";
import { obligationsPath, useAssetObligations, useObligationsCache } from "./useObligations";
import { PartyPicker, type Party, type PartyChange } from "../directory/PartyPicker";
import { ObligationMaterials } from "../materials/ObligationMaterials";

/**
 * Eén formulier voor een uitvoering bij een asset (keuring, inspectie, controle, onderhoud, overig),
 * vanuit het assetpaneel én vanuit de MJOP-planning (docs/ASSETS-MJOP.md, "Uitvoeringen").
 * Nieuw: invullen en *Toevoegen*. Bestaand: elk veld slaat op bij verlaten.
 */
const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");
const euroInput = (c: number | null | undefined) => (c == null ? "" : (c / 100).toFixed(2).replace(".", ","));

type Field =
  | "name"
  | "kind"
  | "basis"
  | "interval"
  | "cost"
  | "lastPerformedOn"
  | "nextDueOn"
  | "endYear"
  | "performer"
  | "overrideReason";
/** `who`: uitvoerder uit het adresboek (bedrijf, persoon) of een vrije naam (`performer`). */
type Values = Record<Field, string> & { notApplicable: boolean; who: Party };

const valuesOf = (o: AssetObligationDto | null): Values => ({
  name: o?.name ?? "",
  kind: o?.kind ?? "onderhoud",
  basis: o?.basis ?? "eigen",
  interval: o ? formatIntervalOf(o.intervalMonths, o.intervalWeeks) : "1 jr",
  cost: euroInput(o?.costCents),
  lastPerformedOn: o?.lastPerformedOn ?? "",
  nextDueOn: o?.nextDueOn ?? "",
  endYear: o?.endYear?.toString() ?? "",
  performer: o?.performerCompanyId ? "" : (o?.performer ?? ""),
  overrideReason: o?.overrideReason ?? "",
  notApplicable: o?.notApplicable ?? false,
  who: {
    companyId: o?.performerCompanyId ?? null,
    personId: o?.performerPersonId ?? null,
    userId: null,
    text: o?.performerCompanyId ? "" : (o?.performer ?? ""),
  },
});

/** Waarde uit het formulier → velden voor de server, of een foutmelding. */
function parse(key: Field, raw: string): { patch: Record<string, unknown> } | { error: string } {
  const v = raw.trim();
  switch (key) {
    case "name":
      return v ? { patch: { name: v } } : { error: "Vul een omschrijving in" };
    case "kind":
      return { patch: { kind: v } };
    case "basis":
      return { patch: { basis: v } };
    case "interval": {
      const iv = parseIntervalInput(v);
      return iv
        ? { patch: { intervalMonths: iv.months, intervalWeeks: iv.weeks } }
        : { error: "Interval als 1 wk, 2 wk, 6 mnd, 5 jr of eenmalig" };
    }
    case "cost": {
      const c = v ? parseEuro(v) : null;
      return v && c === null ? { error: "Bedrag als 1.250 of 1.250,50" } : { patch: { costCents: c } };
    }
    case "lastPerformedOn":
    case "nextDueOn":
      return { patch: { [key]: v || null } };
    case "endYear":
      if (v && !/^\d{4}$/.test(v)) return { error: "Stopjaar als jjjj, of leeg voor doorlopend" };
      return { patch: { endYear: v ? Number(v) : null } };
    case "performer":
      return { patch: { performer: v || null } };
    case "overrideReason":
      return { patch: { overrideReason: v || null } };
  }
}

export function ExecutionPanel({
  assetId,
  buildingId,
  assetLabel,
  obligationId,
  canEdit,
  showCost,
  canReplace = false,
  onReplace,
  onAddReplacement,
  extra,
  onClose,
  onAddAndPlan,
}: {
  /** null = bij het gebouw zelf. */
  assetId: string | null;
  buildingId: string;
  /** Kop van het paneel, bijv. "B6Y-7CG · Personenlift". */
  assetLabel: string;
  /** null = nieuwe uitvoering. */
  obligationId: string | null;
  canEdit: boolean;
  /** Bedragen zien en invoeren (MJOP bekijken). */
  showCost: boolean;
  /** Type *Vervanging* aanbieden: zet de regel om naar de vervanging van de asset. */
  canReplace?: boolean;
  onReplace?: () => void;
  /** Nieuw met type *Vervanging*: maakt de vervanging van de asset; geeft een foutmelding of null. */
  onAddReplacement?: (
    o: Pick<AssetObligationDto, "intervalMonths" | "costCents" | "nextDueOn">,
  ) => Promise<string | null>;
  /** Extra blokken onder het formulier (bijv. de geplande keren in de MJOP). */
  extra?: ReactNode;
  onClose: () => void;
  /** Nieuw: ook *Toevoegen en inplannen*; krijgt het id van de nieuwe uitvoering (issue #261). */
  onAddAndPlan?: (obligationId: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useAssetObligations(buildingId, assetId);
  const setCache = useObligationsCache(buildingId, assetId);
  const o = obligationId ? (q.data?.obligations.find((x) => x.id === obligationId) ?? null) : null;
  const isNew = !obligationId;
  const [v, setV] = useState<Values>(() => valuesOf(o));
  const [loadedFor, setLoadedFor] = useState(o ? o.id : null);
  const [errs, setErrs] = useState<Partial<Record<Field, string>>>({});
  const [busy, setBusy] = useState(false);
  // Bestaande uitvoering die pas na het openen binnenkomt: velden vullen.
  if (o && loadedFor !== o.id) {
    setLoadedFor(o.id);
    setV(valuesOf(o));
  }

  const saved = (r: AssetObligationsResponse) => {
    setCache(r);
    void qc.invalidateQueries({ queryKey: ["mjop"] });
    // Server rekent soms mee (volgende datum uit de laatste keer): velden bijwerken.
    const u = obligationId ? r.obligations.find((x) => x.id === obligationId) : null;
    if (u) setV(valuesOf(u));
  };
  /** Bestaande uitvoering: één veld opslaan. */
  async function saveField(key: Field, value: string) {
    if (isNew || !o) return;
    // Naam typen na een bedrijf uit het adresboek: ook een lege naam maakt de koppeling los.
    if (value === valuesOf(o)[key] && !(key === "performer" && o.performerCompanyId)) return;
    const p = parse(key, value);
    if ("error" in p) return setErrs((e) => ({ ...e, [key]: p.error }));
    setErrs((e) => ({ ...e, [key]: undefined }));
    try {
      saved(await api<AssetObligationsResponse>(`/asset-obligations/${o.id}`, { method: "PATCH", body: p.patch }));
    } catch (e) {
      setErrs((x) => ({ ...x, [key]: errMsg(e) }));
    }
  }
  /** Keuze uit de lijst slaat direct op; een getypte naam bij verlaten van het veld (`saveField`). */
  async function setWho(who: Party, how: PartyChange) {
    setV((x) => ({ ...x, who, performer: who.text }));
    if (isNew || !o || how === "type") return;
    try {
      saved(
        await api<AssetObligationsResponse>(`/asset-obligations/${o.id}`, {
          method: "PATCH",
          body: who.companyId
            ? { performerCompanyId: who.companyId, performerPersonId: who.personId }
            : { performerCompanyId: null, performerPersonId: null, performer: null },
        }),
      );
      setErrs((e) => ({ ...e, performer: undefined }));
    } catch (e) {
      setErrs((x) => ({ ...x, performer: errMsg(e) }));
    }
  }
  async function setNotApplicable(na: boolean) {
    setV((x) => ({ ...x, notApplicable: na }));
    if (isNew || !o) return;
    // Aanzetten vraagt eerst een reden; die slaat op bij verlaten van het veld.
    if (na && !v.overrideReason.trim()) return;
    try {
      saved(
        await api<AssetObligationsResponse>(`/asset-obligations/${o.id}`, {
          method: "PATCH",
          body: { notApplicable: na, overrideReason: na ? v.overrideReason.trim() : null },
        }),
      );
    } catch (e) {
      toast(errMsg(e));
    }
  }
  /** Nieuw met type *Vervanging*: geen uitvoering, maar de vervanging van de asset. */
  async function addReplacement() {
    const next: Partial<Record<Field, string>> = {};
    const iv = parseIntervalInput(v.interval.trim());
    if (!iv) next.interval = "Interval als 1 wk, 2 wk, 6 mnd, 5 jr of eenmalig";
    const c = showCost && v.cost.trim() ? parseEuro(v.cost.trim()) : null;
    if (showCost && v.cost.trim() && c === null) next.cost = "Bedrag als 1.250 of 1.250,50";
    setErrs(next);
    if (!iv || Object.keys(next).length) return;
    setBusy(true);
    const e = await onAddReplacement!({ intervalMonths: iv.months, costCents: c, nextDueOn: v.nextDueOn || null });
    setBusy(false);
    if (!e) onClose();
  }
  async function add(plan = false) {
    if (v.kind === "vervanging") return addReplacement();
    const body: Record<string, unknown> = { id: crypto.randomUUID(), notApplicable: v.notApplicable };
    const next: Partial<Record<Field, string>> = {};
    const fields: Field[] = [
      "name",
      "kind",
      "basis",
      "interval",
      "lastPerformedOn",
      "nextDueOn",
      "endYear",
      ...(v.who.companyId ? [] : (["performer"] as const)),
      "overrideReason",
      ...(showCost ? (["cost"] as const) : []),
    ];
    for (const k of fields) {
      const p = parse(k, v[k]);
      if ("error" in p) next[k] = p.error;
      else Object.assign(body, p.patch);
    }
    if (v.who.companyId)
      Object.assign(body, { performerCompanyId: v.who.companyId, performerPersonId: v.who.personId });
    if (v.notApplicable && !v.overrideReason.trim()) next.overrideReason = "Geef een korte reden";
    setErrs(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    try {
      saved(await api<AssetObligationsResponse>(obligationsPath(buildingId, assetId), { method: "POST", body }));
      toast("Uitvoering toegevoegd");
      if (plan && onAddAndPlan) onAddAndPlan(body.id as string);
      else onClose();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  const field = (key: Field) => ({
    value: v[key],
    disabled: !canEdit,
    "aria-invalid": errs[key] ? true : undefined,
    onChange: (e: { target: { value: string } }) => setV((x) => ({ ...x, [key]: e.target.value })),
    onBlur: (e: { target: { value: string } }) => void saveField(key, e.target.value),
    onKeyDown: (e: { key: string; currentTarget: HTMLElement }) => {
      if (e.key === "Enter" && !isNew) e.currentTarget.blur();
    },
  });
  const err = (key: Field) => errs[key] && <em className="pl-err">{errs[key]}</em>;
  const iv = parseIntervalInput(v.interval);
  const computed = v.lastPerformedOn && iv ? nextDue(v.lastPerformedOn, iv.months, iv.weeks) : null;
  const cost = parseEuro(v.cost);
  const avg = iv ? perYear(cost, iv.months, iv.weeks) : null;

  if (!isNew && !o)
    return (
      <Drawer eyebrow={assetLabel} title="Uitvoering" onClose={onClose}>
        {q.isPending ? <Loading /> : <p className="muted">Deze uitvoering bestaat niet meer.</p>}
      </Drawer>
    );
  return (
    <Drawer
      eyebrow={assetLabel}
      title={isNew ? "Nieuwe uitvoering" : v.name || o!.name}
      sub={
        o && (
          <span className={`pl-status st-${o.status}`}>
            <i aria-hidden="true" />
            {OBLIGATION_STATUS_LABELS[o.status]}
            {o.nextDueOn && ` · volgende ${fmtDay(o.nextDueOn)}`}
          </span>
        )
      }
      onClose={onClose}
      footer={
        isNew ? (
          <>
            <button type="button" className="btn ghost" onClick={onClose}>
              Annuleren
            </button>
            {onAddAndPlan && !v.notApplicable && v.kind !== "vervanging" && (
              <button type="button" className="btn" disabled={busy || !canEdit} onClick={() => void add(true)}>
                Toevoegen en inplannen
              </button>
            )}
            <button type="button" className="btn primary" disabled={busy || !canEdit} onClick={() => void add()}>
              Toevoegen
            </button>
          </>
        ) : (
          <button type="button" className="btn primary" onClick={onClose}>
            Klaar
          </button>
        )
      }
    >
      <form
        className="pl-fgrid"
        aria-label={isNew ? "Nieuwe uitvoering" : `Uitvoering ${o!.name}`}
        onSubmit={(e) => {
          e.preventDefault();
          if (isNew) void add();
        }}
      >
        <label className="field wide">
          <span className="label">Omschrijving *</span>
          <input {...field("name")} data-autofocus autoFocus={isNew} placeholder="bijv. Periodieke keuring lift" />
          {err("name")}
        </label>
        <label className="field">
          <span className="label">Type</span>
          <select
            value={v.kind}
            disabled={!canEdit}
            onChange={(e) => {
              const k = e.target.value;
              if (k === "vervanging" && !isNew) return onReplace?.();
              setV((x) => ({ ...x, kind: k }));
              void saveField("kind", k);
            }}
          >
            {canReplace && (!isNew || onAddReplacement) && (
              <option value="vervanging">{PLAN_TYPE_LABELS.vervanging}</option>
            )}
            {ASSET_PLAN_TYPES.map((k) => (
              <option key={k} value={k}>
                {PLAN_TYPE_LABELS[k as ObligationKind]}
              </option>
            ))}
          </select>
        </label>
        {isNew && v.kind === "vervanging" && (
          <em className="pl-hint">
            Wordt de vervanging van de asset: interval wordt levensduur, per keer de vervangingsprijs en de volgende
            uitvoering het startjaar.
          </em>
        )}
        <label className="field">
          <span className="label">Eis</span>
          <select
            value={v.basis}
            disabled={!canEdit}
            onChange={(e) => {
              setV((x) => ({ ...x, basis: e.target.value }));
              void saveField("basis", e.target.value);
            }}
          >
            {OBLIGATION_BASES.map((b) => (
              <option key={b} value={b}>
                {OBLIGATION_BASIS_APP_LABELS[b as ObligationBasis]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Interval</span>
          <input {...field("interval")} />
          <em className="pl-hint">{errs.interval ?? "1 wk, 2 wk, 6 mnd, 5 jr of eenmalig"}</em>
        </label>
        {showCost && (
          <label className="field">
            <span className="label">Per keer (excl. btw)</span>
            <input {...field("cost")} inputMode="decimal" placeholder="bedrag" />
            <em className="pl-hint">
              {errs.cost ?? (avg != null ? `Gemiddeld ${formatEuro(avg)} per jaar` : "Eenmalig of onbekend")}
            </em>
          </label>
        )}
        <label className="field">
          <span className="label">Laatste keer</span>
          <input {...field("lastPerformedOn")} type="date" />
          <em className="pl-hint">Onbekend mag</em>
        </label>
        <label className="field">
          <span className="label">Volgende uitvoering</span>
          <input {...field("nextDueOn")} type="date" />
          <em className="pl-hint">
            {computed && !v.nextDueOn
              ? `Leeg = ${fmtDay(computed)} (laatste keer + interval)`
              : "Leeg en geen laatste keer: vanaf vandaag"}
          </em>
        </label>
        <label className="field">
          <span className="label">Stopjaar</span>
          <input {...field("endYear")} inputMode="numeric" placeholder="doorlopend" />
          {err("endYear")}
        </label>
        <PartyPicker
          id="obligation-performer"
          className="wide"
          value={v.who}
          onChange={(p, how) => void setWho(p, how)}
          onTextBlur={(text) => void saveField("performer", text)}
          savedName={o?.performer}
          buildingId={buildingId}
          disabled={!canEdit}
          error={errs.performer}
        />
        {o?.contract && (
          <p className="hint wide">
            Valt onder het contract{" "}
            <Link to={`/contracten/${o.contract.contractId}?tab=dekking`}>{o.contract.contractName}</Link>. Een andere
            uitvoerder kiezen kan, het contract blijft gekoppeld.
          </p>
        )}
        <label className="field wide inline">
          <input
            type="checkbox"
            checked={v.notApplicable}
            disabled={!canEdit}
            onChange={(e) => void setNotApplicable(e.target.checked)}
          />
          <span>Niet van toepassing</span>
        </label>
        {v.notApplicable && (
          <label className="field wide">
            <span className="label">Reden *</span>
            <input
              {...field("overrideReason")}
              onBlur={(e) => {
                const r = e.target.value.trim();
                if (isNew || !o || !r) return;
                if (!o.notApplicable)
                  void api<AssetObligationsResponse>(`/asset-obligations/${o.id}`, {
                    method: "PATCH",
                    body: { notApplicable: true, overrideReason: r },
                  }).then(saved, (x) => toast(errMsg(x)));
                else void saveField("overrideReason", r);
              }}
              placeholder="bijv. valt niet onder het Warenwetbesluit liften"
            />
            {err("overrideReason")}
          </label>
        )}
        {/* Verborgen knop: Enter in een veld voegt een nieuwe uitvoering toe. */}
        <button type="submit" hidden />
      </form>
      {o && <ObligationMaterials obligationId={o.id} buildingId={buildingId} canEdit={canEdit} showCost={showCost} />}
      {extra}
    </Drawer>
  );
}
